import api from './api';

export interface TrustHubProfile {
  userId: string;
  username: string;
  displayName: string | null;
  email: string | null;
  phoneNumber: string | null;
  dateOfBirth: string | null;
  legalFirstName: string | null;
  legalLastName: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  businessName: string | null;
  businessType: string | null;
  industry: string | null;
  websiteUrl: string | null;
  registrationAuthority: string | null;
  registrationNumber: string | null;
  created: string;
  lastModified: string;
}

export interface UpdateProfileRequest {
  displayName?: string;
  phoneNumber?: string;
  legalFirstName?: string;
  legalLastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  businessName?: string;
  industry?: string;
  websiteUrl?: string;
}

export interface VerificationItem {
  type: string;
  isVerified: boolean;
  verifiedDate: string | null;
  method: string | null;
  canTrigger: boolean;
  triggerAction: string | null;
}

export interface VerificationStatus {
  userId: string;
  overallVerificationLevel: number;
  verificationLevelName: string;
  email: VerificationItem;
  phone: VerificationItem;
  identity: VerificationItem;
  address: VerificationItem;
  creditCard: VerificationItem | null;
  bankAccount: VerificationItem | null;
  trustScore: number;
  trustScoreLevel: string;
}

export interface TrustHubCredential {
  credentialId: string;
  credentialType: string;
  username: string;
  isBusinessAccount: boolean;
  businessName: string | null;
  status: number;
  statusName: string;
  created: string;
  lastLogin: string | null;
  paymentComplete: boolean;
  is2FAEnabled: boolean;
}

export interface VerificationHistory {
  historyId: string;
  verificationType: string;
  action: string;
  method: string | null;
  levelBefore: number | null;
  levelAfter: number | null;
  actionDate: string;
  notes: string | null;
}

export interface TrustScoreFactor {
  factor: string;
  points: number;
  description: string;
}

export interface TrustScore {
  userId: string;
  score: number;
  level: string;
  lastCalculated: string;
  accountAgePoints: number;
  loginHistoryPoints: number;
  verificationPoints: number;
  penaltyPoints: number;
  factors: TrustScoreFactor[];
}

export interface TrustHubDashboard {
  profile: TrustHubProfile | null;
  verificationStatus: VerificationStatus;
  credentials: TrustHubCredential[];
  trustScore: TrustScore | null;
  hasCredentials: boolean;
  canAddCredentials: boolean;
}

const trustHubService = {
  async getProfile(): Promise<{ success: boolean; profile: TrustHubProfile | null }> {
    return api.get('/api/TrustHub/profile');
  },

  async updateProfile(request: UpdateProfileRequest): Promise<{ success: boolean; message?: string }> {
    return api.put('/api/TrustHub/profile', request);
  },

  async getVerificationStatus(): Promise<{ success: boolean; status: VerificationStatus }> {
    return api.get('/api/TrustHub/verification-status');
  },

  async getCredentials(): Promise<{ success: boolean; credentials: TrustHubCredential[] }> {
    return api.get('/api/TrustHub/credentials');
  },

  async triggerVerification(verificationType: string): Promise<{ success: boolean; message?: string }> {
    return api.post(`/api/TrustHub/verify/${verificationType}`);
  },

  async getVerificationHistory(): Promise<{ success: boolean; history: VerificationHistory[] }> {
    return api.get('/api/TrustHub/verification-history');
  },

  async getTrustScore(): Promise<{ success: boolean; score: TrustScore | null }> {
    return api.get('/api/TrustHub/trust-score');
  },

  async getDashboard(): Promise<{ success: boolean; dashboard: TrustHubDashboard }> {
    return api.get('/api/TrustHub/dashboard');
  }
};

export default trustHubService;
